// Client-side invoice → PDF using jsPDF + html2canvas.
// Captures an existing DOM element (the styled invoice view) and produces
// a high-resolution multi-page A4 PDF that matches the on-screen design.

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MARGIN_MM = 12;

export async function exportInvoicePdf(elementId, filename = 'invoice.pdf') {
  const el = document.getElementById(elementId);
  if (!el) throw new Error(`Element #${elementId} not found`);

  // Render at 2x for crisp output. Use white background to avoid transparency artifacts.
  const canvas = await html2canvas(el, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    windowWidth: el.scrollWidth,
  });

  const imgData = canvas.toDataURL('image/jpeg', 0.95);
  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });

  const usableWidth = A4_WIDTH_MM - MARGIN_MM * 2;
  const imgHeight = (canvas.height * usableWidth) / canvas.width;
  const usableHeight = A4_HEIGHT_MM - MARGIN_MM * 2;

  if (imgHeight <= usableHeight) {
    pdf.addImage(imgData, 'JPEG', MARGIN_MM, MARGIN_MM, usableWidth, imgHeight);
  } else {
    // Multi-page: slice the canvas vertically per A4 page.
    const pxPerMm = canvas.width / usableWidth;
    const pageHeightPx = usableHeight * pxPerMm;
    let renderedPx = 0;
    while (renderedPx < canvas.height) {
      const sliceHeight = Math.min(pageHeightPx, canvas.height - renderedPx);
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sliceHeight;
      const ctx = sliceCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      ctx.drawImage(canvas, 0, renderedPx, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
      const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.95);
      const sliceMm = sliceHeight / pxPerMm;
      if (renderedPx > 0) pdf.addPage();
      pdf.addImage(sliceData, 'JPEG', MARGIN_MM, MARGIN_MM, usableWidth, sliceMm);
      renderedPx += sliceHeight;
    }
  }

  pdf.save(filename);
}
